import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2, CheckCircle, XCircle, Loader2, Trash2, Eye, EyeOff, ExternalLink, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DATEVIntegration() {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [status, setStatus] = useState(null); // null | 'loading' | 'connected' | 'error' | 'saved' | 'not_configured'
  const [message, setMessage] = useState('');
  const [existing, setExisting] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/integrations/datev/status`).then(r => {
      setExisting(r.data);
      setStatus(r.data.status);
      if (r.data.client_id) setClientId(r.data.client_id);
    }).catch(() => {});
  }, []);

  const handleSaveAndTest = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setStatus('error');
      setMessage('Both Client ID and Client Secret are required.');
      return;
    }
    setTesting(true);
    setStatus('loading');
    setMessage('');
    try {
      const res = await axios.post(`${API}/integrations/datev/test`, { client_id: clientId, client_secret: clientSecret });
      setStatus(res.data.status);
      setMessage(res.data.message);
      setExisting({ ...existing, status: res.data.status, client_id: clientId, last_sync_at: new Date().toISOString() });
    } catch (e) {
      setStatus('error');
      setMessage(e.response?.data?.detail || 'Connection failed.');
    }
    setTesting(false);
  };

  const handleDisconnect = async () => {
    await axios.delete(`${API}/integrations/datev`);
    setStatus('not_configured');
    setClientId('');
    setClientSecret('');
    setMessage('');
    setExisting(null);
  };

  const statusColor = status === 'connected' ? 'text-green-600' : status === 'error' ? 'text-red-500' : status === 'saved' ? 'text-amber-500' : 'text-muted-foreground';
  const StatusIcon = status === 'connected' ? CheckCircle : status === 'error' ? XCircle : status === 'loading' ? Loader2 : Link2;

  return (
    <div data-testid="datev-integration-page" className="space-y-6 max-w-2xl">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <Link2 className="w-6 h-6 text-accent" /> DATEV Integration
        </h1>
        <Link to="/help/datev-integration" data-testid="datev-help-link">
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-1.5" /> Setup Guide
          </Button>
        </Link>
      </div>

      {/* Status Card */}
      <Card className={`p-5 animate-fade-in ${status === 'connected' ? 'border-green-500/30 bg-green-500/[0.03]' : ''}`} data-testid="datev-status-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-5 h-5 ${statusColor} ${status === 'loading' ? 'animate-spin' : ''}`} />
            <div>
              <p className="text-sm font-medium">Connection Status</p>
              <p className={`text-xs ${statusColor} capitalize`} data-testid="datev-status-text">
                {status === 'not_configured' ? 'Not Configured' : status === 'loading' ? 'Testing...' : status || 'Not Configured'}
              </p>
            </div>
          </div>
          {existing?.last_sync_at && (
            <p className="text-[11px] text-muted-foreground">
              Last tested: {new Date(existing.last_sync_at).toLocaleString()}
            </p>
          )}
        </div>
      </Card>

      {/* Credentials Form */}
      <Card className="p-6 animate-fade-in" data-testid="datev-credentials-form">
        <h3 className="text-sm font-heading font-semibold mb-4">DATEV API Credentials</h3>
        <p className="text-xs text-muted-foreground mb-5">
          Enter your DATEV API credentials below. These will be encrypted and stored securely.
          You can obtain credentials from the DATEV Developer Portal.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="datev-client-id">Client ID</Label>
            <Input
              id="datev-client-id"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder="e.g. datev-app-xxxxxxxxxxxx"
              data-testid="datev-client-id-input"
            />
          </div>

          <div>
            <Label htmlFor="datev-client-secret">Client Secret</Label>
            <div className="relative">
              <Input
                id="datev-client-secret"
                type={showSecret ? 'text' : 'password'}
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder="Enter your client secret..."
                className="pr-10"
                data-testid="datev-client-secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="datev-toggle-secret"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${status === 'connected' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`} data-testid="datev-message">
              {status === 'connected' ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              {message}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSaveAndTest}
              disabled={testing}
              className="bg-primary hover:bg-primary/90"
              data-testid="datev-save-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save & Test
            </Button>
            {status === 'connected' && (
              <Button variant="outline" onClick={handleDisconnect} className="text-destructive hover:text-destructive" data-testid="datev-disconnect-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Info */}
      <Card className="p-5 bg-muted/50 animate-fade-in" data-testid="datev-info-card">
        <h4 className="text-xs font-heading font-semibold mb-2 text-muted-foreground uppercase tracking-wider">BYOL Model</h4>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Veridex uses a Bring Your Own License (BYOL) model for DATEV integration.
          You are responsible for your own DATEV subscription and API access fees.
          We only store your credentials (encrypted) to facilitate the connection.
        </p>
        <Link to="/help/datev-integration" className="inline-flex items-center gap-1 mt-2 text-xs text-accent hover:underline" data-testid="datev-learn-more">
          <ExternalLink className="w-3 h-3" /> Learn how to get credentials
        </Link>
      </Card>
    </div>
  );
}
